import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORG_TYPES = [
  "Police Department / Sheriff's Office", "Fire Department", "EMS Agency",
  "Trauma Center / Hospital", "University / Medical School", "Simulation Center",
  "Emergency Management Agency", "Other Public Safety or Research Organization",
];

const INTERESTS = [
  "Technical Review", "Pilot Site", "Medical Direction", "Independent Evaluation",
  "Simulation Support", "Research Collaboration", "Training Partnership",
  "Emergency Management Coordination", "General Inquiry",
];

const initial = { name: "", title: "", organization: "", org_type: "", email: "", phone: "", interest: "", message: "", consent: false };

const inputCls = "w-full bg-surface border border-line focus:border-signal outline-none text-white text-sm px-4 py-3.5 placeholder:text-slate-500 transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-2.5";

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.title || !form.organization || !form.org_type || !form.email || !form.interest) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm the acknowledgment checkbox.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Request received. The T-EMRP team will follow up with the requested information.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page">
      <PageHead
        chapter="11"
        eyebrow="Contact"
        titleLines={["Request more", "information."]}
        lede="Share a few details about your organization and area of interest, and the T-EMRP team will follow up with the requested information."
      />

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-16">
        {sent ? (
          <Reveal>
            <div data-testid="contact-success" className="border border-line bg-surface p-12 text-left">
              <CheckCircle2 size={40} className="text-signal" />
              <h2 className="mt-6 font-display font-extrabold text-3xl text-white tracking-tight">Request received.</h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">
                Thank you, {form.name.split(" ")[0]}. Your request has been recorded and the T-EMRP team will follow up at {form.email} with the requested information.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => { setForm(initial); setSent(false); }}
                className="mt-8 inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors"
              >
                Send another request
              </button>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <form onSubmit={submit} data-testid="contact-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className={labelCls}>Full name *</label>
                  <input id="name" data-testid="contact-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                </div>
                <div>
                  <label htmlFor="title" className={labelCls}>Title *</label>
                  <input id="title" data-testid="contact-input-title" className={inputCls} value={form.title} onChange={set("title")} placeholder="Chief of Operations" />
                </div>
                <div>
                  <label htmlFor="organization" className={labelCls}>Organization *</label>
                  <input id="organization" data-testid="contact-input-organization" className={inputCls} value={form.organization} onChange={set("organization")} placeholder="Metro Fire Department" />
                </div>
                <div>
                  <label htmlFor="org_type" className={labelCls}>Organization type *</label>
                  <select id="org_type" data-testid="contact-select-org-type" className={inputCls} value={form.org_type} onChange={set("org_type")}>
                    <option value="">Select one</option>
                    {ORG_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="email" className={labelCls}>Email *</label>
                  <input id="email" type="email" data-testid="contact-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="jane@agency.gov" />
                </div>
                <div>
                  <label htmlFor="phone" className={labelCls}>Phone</label>
                  <input id="phone" type="tel" data-testid="contact-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="interest" className={labelCls}>Area of interest *</label>
                <select id="interest" data-testid="contact-select-interest" className={inputCls} value={form.interest} onChange={set("interest")}>
                  <option value="">Select one</option>
                  {INTERESTS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              <div className="mt-6">
                <label htmlFor="message" className={labelCls}>Message</label>
                <textarea
                  id="message"
                  data-testid="contact-textarea-message"
                  rows={5}
                  className={inputCls}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your interagency readiness priorities or the information you would like to receive."
                />
              </div>

              <label className="mt-6 flex items-start gap-4 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid="contact-checkbox-consent"
                  checked={form.consent}
                  onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                  className="mt-1 accent-[#DC2626] w-4 h-4 shrink-0"
                />
                <span className="text-xs text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                  I understand that T-EMRP is a proposed, pre-pilot framework and that this message does not constitute a partnership agreement or commitment by either party. *
                </span>
              </label>

              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="mt-10 inline-flex items-center gap-3 bg-signal hover:bg-signal-hover disabled:opacity-60 text-white font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <ArrowUpRight size={15} />}
                {loading ? "Sending..." : "Request More Information"}
              </button>
            </form>
          </Reveal>
        )}

        <Reveal delay={0.15}>
          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="border border-line bg-surface p-8">
              <Eyebrow>What happens next</Eyebrow>
              <ul className="mt-6 space-y-5">
                {["Your request is reviewed and the relevant materials are prepared.", "The T-EMRP team follows up by email with the requested information.", "If there is mutual interest, a bounded, reversible role is scoped."].map((t, i) => (
                  <li key={i} className="flex gap-4 text-sm text-slate-300 leading-relaxed">
                    <span className="font-mono text-xs text-signal shrink-0 mt-0.5">0{i + 1}</span> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border border-line bg-surface p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-3">Email</p>
              <a href="mailto:gabriel.galvani@temrp.org" data-testid="contact-email-link" className="font-display font-bold text-white tracking-tight hover:text-signal transition-colors">
                gabriel.galvani@temrp.org
              </a>
            </div>
            <div className="border-l-2 border-signal bg-surface p-6">
              <p className="text-xs text-mist leading-relaxed">
                An information request carries no commitment. Most institutions begin with a technical review — 2 to 4 hours of expert time — before deciding on any further role.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
